/* eslint-disable rxjs/no-ignored-observable */
import { Injectable } from '@angular/core';
import { UserLimit, UserSubscription, WorkflowRunStatusEvent } from '@ca/core/models';
import { ExperimentalFeaturesService, MessagingService, WorkflowRunQueueService } from '@ca/core/services';
import { BehaviorSubject, iif, merge, Observable, timer } from 'rxjs';
import { delay, switchMap, take, tap } from 'rxjs/operators';
import { Notification, NotificationBuilder } from '../../../core/models/notification.model';
import { UserService } from '../../../core/services/user.service';
import { UserLimitKind } from '../../../core/types/user-limit-kind.type';
import { NotificationsFactory } from '../notifications.factory';

export const LIMITS_TOPIC: string = 'limits_topic';

@Injectable({
    providedIn: 'root',
})
export class NotificationsService {
    private static INTERVAL: number = 60 * 1000;
    private notificationsMap = new Map<string, Notification>();
    // eslint-disable-next-line rxjs/no-explicit-generics
    private notifications$$ = new BehaviorSubject<Map<string, Notification>>(this.notificationsMap);

    get notifications$(): Observable<Map<string, Notification>> {
        return this.notifications$$.asObservable();
    }

    constructor(
        private notificationsFactory: NotificationsFactory,
        private userService: UserService,
        private workflowRunQueueService: WorkflowRunQueueService,
        private messagingService: MessagingService,
        private experimentalFeaturesService: ExperimentalFeaturesService,
    ) {
        if (this.experimentalFeaturesService.isLimitCheckingActive) {
            this.messagingService.subscription(LIMITS_TOPIC);
            this.pullNotifications();
        }
    }

    buildNotifications(builders: NotificationBuilder[], isTrial: boolean): void {
        builders.forEach(builder => {
            if (builder.shouldBuildNotification()) {
                const notification: Notification = builder.build(isTrial);
                this.notificationsMap.set(builder.getType(), notification);
            } else {
                if (this.notificationsMap.has(builder.getType())) {
                    this.notificationsMap.delete(builder.getType());
                }
            }
        });
    }

    pullNotifications(): void {
        merge(timer(0, NotificationsService.INTERVAL), this.workflowRunQueueService.workflowEvent$)
            .pipe(
                switchMap((val: number | WorkflowRunStatusEvent) =>
                    iif(
                        (): boolean => typeof val === 'object' && val.stateDescription === 'CompletedCancelled',
                        this.userService.getUserLimits$().pipe(delay(5000)),
                        this.userService.getUserLimits$(),
                    ),
                ),
                tap((limits: Map<UserLimitKind, UserLimit | string>): void => {
                    this.userService.userSubscription$.pipe(take(1)).subscribe((userSubscription: UserSubscription): void => {
                        const isTrial = userSubscription.name.toLowerCase().includes('trial');

                        this.messagingService.publish<Map<UserLimitKind, UserLimit | string>>(LIMITS_TOPIC, { action: 'limits', payload: limits });
                        const builders: NotificationBuilder[] = [
                            ...this.notificationsFactory.getLimitsNotificationBuilders(limits),
                            ...this.notificationsFactory.getSubscriptionEndsNotificationBuilders(limits),
                        ];
                        this.buildNotifications(builders, isTrial);
                        this.notifications$$.next(this.notificationsMap);
                    });
                }),
            )
            .subscribe();
    }
}
